import React, {useEffect} from 'react'
import {
  Caption,
  Col,
  Row,
  InlineLoader,
  P,
  H6,
  HelpIcon,
  withTooltip,
  ButtonLink,
  InfoAlert,
} from '@modifi/ui'
import {useTranslation, FormattedNumber} from '@modifi/plugin-i18n'
import {landGreen, landGreenLight, seaNavy, seaNavyLight} from '@modifi/colors'
import {OnboardingActivationStatus} from '@modifi/enums'

import {Wrapper, LimitCard} from './styles'
import {companyActivationStatusMapping} from './constants'
import TasksList from './components/tasks-list'
import DynamicEntriesTable from '../../../component/dynamic-entries-table'
import SummaryPageFooter from '../components/summary-page-footer'
import {SECTION_OVERVIEW} from '../../../constants/sections'
import {TradePartnerSummaryEntry} from '../../seller-trade-partners/seller-trade-partner-summary/selector'

const tableHeadlines = ['name', 'approvalStatus', 'allocatedLimit']

const TooltipIcon = withTooltip(HelpIcon) as React.ComponentType<any>

export type OverviewProps = {
  activationStatus?: keyof typeof companyActivationStatusMapping | null
  showBuyers?: boolean
  sumOfSellerTradePartnerLimits: number
  annualFundingPotential: number
  tradePartnerLimitCurrency: string
  sellerTradePartnerSummary: TradePartnerSummaryEntry[]
  fetchTasks: () => void
  redirectToBuyers: () => void
  areAllBsrsDeclined: boolean
}

const Overview: React.FC<OverviewProps> = ({
  activationStatus = null,
  showBuyers = false,
  sumOfSellerTradePartnerLimits,
  annualFundingPotential,
  tradePartnerLimitCurrency,
  sellerTradePartnerSummary,
  fetchTasks,
  redirectToBuyers,
  areAllBsrsDeclined,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  if (activationStatus === OnboardingActivationStatus.INCOMPLETE) {
    // when user is supposed to be redirected to welcome page
    return (
      <Row justifyContent="center" my={100}>
        <Col auto>
          <InlineLoader />
        </Col>
      </Row>
    )
  }

  return (
    <>
      <Wrapper>
        <Row>
          <Col auto mb={16}>
            <LimitCard bgColor={landGreenLight} highlightColor={landGreen}>
              <Caption>
                {t('overview.limit-card.total-allocated-limit.label')}
                <TooltipIcon
                  tooltipContent={t(`overview.limit-card.total-allocated-limit.tooltip`)}
                />
              </Caption>

              <H6 mb={0}>
                {tradePartnerLimitCurrency && (
                  <FormattedNumber currency={tradePartnerLimitCurrency}>
                    {sumOfSellerTradePartnerLimits}
                  </FormattedNumber>
                )}
              </H6>
            </LimitCard>
          </Col>
          <Col auto mb={16}>
            <LimitCard bgColor={seaNavyLight} highlightColor={seaNavy}>
              <Caption>
                {t('overview.limit-card.annual-funding-potential.label')}
                <TooltipIcon
                  tooltipContent={t(`overview.limit-card.annual-funding-potential.tooltip`)}
                />
              </Caption>
              <H6 mb={0}>
                {tradePartnerLimitCurrency && (
                  <FormattedNumber currency={tradePartnerLimitCurrency}>
                    {annualFundingPotential}
                  </FormattedNumber>
                )}
              </H6>
            </LimitCard>
          </Col>
        </Row>

        <TasksList />

        {showBuyers && areAllBsrsDeclined && (
          <Row>
            <Col>
              <InfoAlert>{t('overview.all-buyers-declined.info-alert.text')}</InfoAlert>
            </Col>
          </Row>
        )}

        {showBuyers && sellerTradePartnerSummary.length > 0 && (
          <>
            <H6 mt={16}>{t('overview.buyers-list.headline')}</H6>
            <P mb={16}>{t('overview.buyers-list.description')}</P>

            <DynamicEntriesTable
              dataList={sellerTradePartnerSummary}
              headlines={tableHeadlines}
              transPrefix="seller-trade-partner-summary-page"
            />
            <Row justifyContent="center">
              <Col auto>
                <ButtonLink data-target="buyers-section" onClick={redirectToBuyers}>
                  {t('overview.buyers-list.cta')}
                </ButtonLink>
              </Col>
            </Row>
          </>
        )}
      </Wrapper>
      <SummaryPageFooter
        sectionName={SECTION_OVERVIEW}
        translationKey="sub-section-menu.overview.bottom-cta.text"
      />
    </>
  )
}

export default Overview
